import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  ShieldCheck,
  Droplets,
  Sun,
  Moon,
  Clock,
  CheckCircle2,
  Truck,
  Building2,
  Banknote,
  Star,
  ChevronRight,
  Layers,
  FlaskConical,
  Award
} from "lucide-react";
import { api, mediaUrl } from "../services/api";
import ProductCard from "../components/product/ProductCard";
import Reveal from "../components/Reveal";

import { useLanguage } from "../context/LanguageContext";

// Brand imagery assets
import heroDuo from "../assets/brand/products-duo-transparent.png";
import vitcBottle from "../assets/brand/vitaminc-bottle-transparent.png";
import hyalBottle from "../assets/brand/hyaluronic-bottle-transparent.png";
import product1Hero from "../assets/brand/product1.jpg";
import product2Hero from "../assets/brand/product2.jpg";
import beforeAfterBanner from "../assets/brand/before_after.jpg";
import avantFace from "../assets/brand/avant_face.jpg";
import apresFace from "../assets/brand/apres_face.jpg";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef(null);
  const { t, isRTL } = useLanguage();

  useEffect(() => {
    api.get("/products")
      .then((res) => {
        setProducts(res.data.products || []);
      })
      .catch((err) => {
        console.error("Error fetching products:", err);
      });
  }, []);

  // Handle Before/After slider dragging
  const handleSliderMove = (clientX) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percent = Math.max(10, Math.min(90, (x / rect.width) * 100));
    setSliderPosition(percent);
  };

  const handleTouchMove = (e) => {
    if (e.touches && e.touches[0]) {
      handleSliderMove(e.touches[0].clientX);
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      handleSliderMove(e.clientX);
    }
  };

  return (
    <div id="main" className="overflow-hidden bg-white text-[#111111]">
      
      {/* 1. HERO SECTION — CLEAN LUXURY EDITORIAL SHOWCASE (SINGLE RESTORED HERO) */}
      <section className="relative min-h-[92vh] flex items-center pt-28 pb-16 bg-gradient-to-b from-[#FAF8F5] via-[#F8F8F6] to-white overflow-hidden">
        {/* Soft Ambient Glows */}
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-[#D97706]/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-1/3 -right-32 w-96 h-96 bg-[#0284C7]/5 rounded-full blur-3xl pointer-events-none" />

        <div className="container-queen relative grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Hero Content */}
          <div className="lg:col-span-7 space-y-7 z-10">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#EAE8E4] bg-white/90 shadow-sm"
            >
              <Sparkles size={13} className="text-[#C5A880]" />
              <span className="text-[11px] font-body tracking-[0.2em] uppercase text-[#111111] font-medium">
                {t("hero_badge")}
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="font-display text-[#111111] leading-[1.05] tracking-[-0.02em]"
              style={{ fontSize: "clamp(3.2rem, 6.5vw, 5.8rem)" }}
            >
              {t("hero_title_1")} <br />
              <span className="font-light italic text-[#333333]">{t("hero_title_2")}</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-[#666666] font-body text-base sm:text-lg max-w-xl leading-relaxed"
            >
              {t("hero_desc")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.45 }}
              className="flex flex-wrap items-center gap-4 pt-2"
            >
              <Link to="/shop" className="btn-queen-primary shadow-lg shadow-black/10">
                {t("hero_cta_discover")} <ArrowRight size={16} className={isRTL ? "rotate-180" : ""} />
              </Link>
              <a href="#routine" className="btn-queen-secondary">
                {t("hero_cta_routine")}
              </a>
            </motion.div>

            {/* Micro Feature Statistics */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="grid grid-cols-3 gap-6 pt-6 border-t border-[#EAE8E4] max-w-lg"
            >
              <div>
                <p className="font-display text-2xl font-normal text-[#111111]">{t("hero_stat_1_val")}</p>
                <p className="text-[11px] font-body uppercase tracking-[0.12em] text-[#666666] mt-0.5">
                  {t("hero_stat_1_lbl")}
                </p>
              </div>
              <div>
                <p className="font-display text-2xl font-normal text-[#111111]">{t("hero_stat_2_val")}</p>
                <p className="text-[11px] font-body uppercase tracking-[0.12em] text-[#666666] mt-0.5">
                  {t("hero_stat_2_lbl")}
                </p>
              </div>
              <div>
                <p className="font-display text-2xl font-normal text-[#111111]">{t("hero_stat_3_val")}</p>
                <p className="text-[11px] font-body uppercase tracking-[0.12em] text-[#666666] mt-0.5">
                  {t("hero_stat_3_lbl")}
                </p>
              </div>
            </motion.div>
          </div>

          {/* Right Product Composition Stage — Clean, High-Resolution, Single Stage */}
          <div className="lg:col-span-5 relative flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="relative w-full max-w-[480px] aspect-[4/5] flex items-center justify-center"
            >
              {/* Stone Pedestal Effect */}
              <div 
                className="absolute bottom-6 w-[88%] h-14 bg-gradient-to-r from-[#D8D4CC] via-[#EDE9E1] to-[#D8D4CC] rounded-full blur-[1px] shadow-2xl opacity-80"
                style={{ transform: "rotateX(70deg)" }}
              />

              {/* Main Cutout Composition */}
              <img
                src={heroDuo}
                alt="The Queen Sérum Vitamine C et Sérum Acide Hyaluronique"
                className="relative z-10 w-full h-auto object-contain filter drop-shadow-[0_35px_45px_rgba(0,0,0,0.18)]"
              />

              {/* Floating Luxury Annotation 1: Vitamin C */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.7 }}
                className="absolute -left-4 top-1/4 z-20 bg-white/95 backdrop-blur-md border border-[#EAE8E4] px-4 py-2.5 rounded-2xl shadow-lg hidden sm:flex items-center gap-3"
              >
                <div className="w-8 h-8 rounded-full bg-[#FEF3C7] grid place-items-center text-[#D97706]">
                  <Sun size={16} />
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#111111] leading-none">{t("hotspot_vitc")}</p>
                  <p className="text-[10px] text-[#666666] mt-0.5">{t("hotspot_vitc_sub")}</p>
                </div>
              </motion.div>

              {/* Floating Luxury Annotation 2: Hyaluronic */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.85 }}
                className="absolute -right-4 bottom-1/4 z-20 bg-white/95 backdrop-blur-md border border-[#EAE8E4] px-4 py-2.5 rounded-2xl shadow-lg hidden sm:flex items-center gap-3"
              >
                <div className="w-8 h-8 rounded-full bg-[#E0F2FE] grid place-items-center text-[#0284C7]">
                  <Droplets size={16} />
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#111111] leading-none">{t("hotspot_hyal")}</p>
                  <p className="text-[10px] text-[#666666] mt-0.5">{t("hotspot_hyal_sub")}</p>
                </div>
              </motion.div>
            </motion.div>
          </div>

        </div>
      </section>

      {/* 2. BRAND STORY / PHILOSOPHY */}
      <section className="py-24 bg-[#F8F8F6] border-y border-[#EAE8E4]">
        <div className="container-queen">
          <div className="grid lg:grid-cols-12 gap-14 items-center">
            
            <Reveal className="lg:col-span-6 relative">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl bg-white border border-[#EAE8E4]">
                <img
                  src={product1Hero}
                  alt="La Maison The Queen — Sérum Vitamine C"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white border border-[#EAE8E4] p-6 rounded-2xl shadow-xl max-w-xs hidden sm:block">
                <p className="font-display text-xl text-[#111111] italic leading-tight">
                  « Chaque goutte est une célébration de la science dermatologique. »
                </p>
                <p className="text-[11px] font-body uppercase tracking-[0.16em] text-[#888888] mt-3">
                  Laboratoires The Queen
                </p>
              </div>
            </Reveal>

            <Reveal className="lg:col-span-6 space-y-6">
              <p className="label-eyebrow text-[#C5A880]">{t("story_badge")}</p>
              <h2 className="font-display text-[#111111] text-3xl sm:text-5xl leading-[1.12]">
                {t("story_title")}
              </h2>
              <p className="text-[#666666] font-body text-base leading-relaxed">
                {t("story_desc_1")}
              </p>
              <p className="text-[#666666] font-body text-base leading-relaxed">
                {t("story_desc_2")}
              </p>

              <div className="grid grid-cols-2 gap-6 pt-4">
                <div className="border-l-2 border-[#111111] pl-4">
                  <h4 className="font-display text-xl text-[#111111]">{t("story_feat_1")}</h4>
                  <p className="text-xs text-[#666666] mt-1">{t("story_feat_2")}</p>
                </div>
                <div className="border-l-2 border-[#111111] pl-4">
                  <h4 className="font-display text-xl text-[#111111]">{t("story_feat_3")}</h4>
                  <p className="text-xs text-[#666666] mt-1">{t("story_feat_4")}</p>
                </div>
              </div>
            </Reveal>

          </div>
        </div>
      </section>

      {/* 3. FEATURED PRODUCTS (THE COLLECTION) */}
      <section className="py-24 bg-white" id="collection">
        <div className="container-queen">
          <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between mb-14">
            <div>
              <p className="label-eyebrow text-[#C5A880] mb-2">Les Soins Iconiques</p>
              <h2 className="font-display text-[#111111] text-3xl sm:text-5xl">La Collection</h2>
            </div>
            <Link to="/shop" className="btn-queen-ghost mt-4 sm:mt-0">
              Voir tout le catalogue <ArrowRight size={16} />
            </Link>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((p, idx) => (
              <Reveal key={p.id} delay={idx * 100}>
                <ProductCard product={p} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 4. INGREDIENTS SECTION (SCIENTIFIC STORYTELLING) */}
      <section className="py-24 bg-[#F8F8F6] border-t border-[#EAE8E4]" id="ingredients">
        <div className="container-queen">
          <Reveal className="text-center max-w-2xl mx-auto mb-16">
            <p className="label-eyebrow text-[#C5A880] mb-2">Haute Formulation</p>
            <h2 className="font-display text-[#111111] text-3xl sm:text-5xl">
              Au Cœur de la Formule
            </h2>
            <p className="text-[#666666] font-body text-base mt-4">
              Des actifs purs de grade pharmaceutique, dosés à la concentration optimale pour des résultats visibles et durables.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            
            {/* Ingredient 1 */}
            <Reveal delay={100} className="bg-white rounded-2xl p-7 border border-[#EAE8E4] flex flex-col justify-between hover:shadow-lg transition-all">
              <div>
                <div className="w-12 h-12 rounded-full bg-[#FEF3C7] text-[#D97706] grid place-items-center mb-6">
                  <Sun size={24} />
                </div>
                <p className="text-xs font-body uppercase tracking-[0.14em] text-[#D97706] font-medium">15% Concentration</p>
                <h3 className="font-display text-2xl text-[#111111] mt-1">Vitamine C Pure</h3>
                <p className="text-xs text-[#666666] leading-relaxed mt-3">
                  Acide L-ascorbique stabilisé. Stimule la synthèse des fibres de collagène, unifie les irrégularités pigmentaires et neutralise les radicaux libres responsables du vieillissement.
                </p>
              </div>
              <div className="pt-6 mt-6 border-t border-[#F8F8F6] text-[11px] text-[#111111] font-medium">
                Action : Éclat &amp; Fermeté
              </div>
            </Reveal>

            {/* Ingredient 2 */}
            <Reveal delay={200} className="bg-white rounded-2xl p-7 border border-[#EAE8E4] flex flex-col justify-between hover:shadow-lg transition-all">
              <div>
                <div className="w-12 h-12 rounded-full bg-[#E0F2FE] text-[#0284C7] grid place-items-center mb-6">
                  <Droplets size={24} />
                </div>
                <p className="text-xs font-body uppercase tracking-[0.14em] text-[#0284C7] font-medium">Multi-Moléculaire</p>
                <h3 className="font-display text-2xl text-[#111111] mt-1">Acide Hyaluronique</h3>
                <p className="text-xs text-[#666666] leading-relaxed mt-3">
                  Trois poids moléculaires distincts. Pénètre en surface pour lisser instantanément les ridules de déshydratation et en profondeur pour reconstituer le matelas hydrique cellulaire.
                </p>
              </div>
              <div className="pt-6 mt-6 border-t border-[#F8F8F6] text-[11px] text-[#111111] font-medium">
                Action : Repulpage 24h
              </div>
            </Reveal>

            {/* Ingredient 3 */}
            <Reveal delay={300} className="bg-white rounded-2xl p-7 border border-[#EAE8E4] flex flex-col justify-between hover:shadow-lg transition-all">
              <div>
                <div className="w-12 h-12 rounded-full bg-[#F3F4F6] text-[#111111] grid place-items-center mb-6">
                  <ShieldCheck size={24} />
                </div>
                <p className="text-xs font-body uppercase tracking-[0.14em] text-[#111111] font-medium">Synergie Protectrice</p>
                <h3 className="font-display text-2xl text-[#111111] mt-1">Vitamine E Pure</h3>
                <p className="text-xs text-[#666666] leading-relaxed mt-3">
                  Tocophérol naturel. Renforce la barrière lipidique cutanée et multiplie par quatre la stabilité et l'efficacité antioxydante de la Vitamine C.
                </p>
              </div>
              <div className="pt-6 mt-6 border-t border-[#F8F8F6] text-[11px] text-[#111111] font-medium">
                Action : Bouclier Cellulaire
              </div>
            </Reveal>

            {/* Ingredient 4 */}
            <Reveal delay={400} className="bg-white rounded-2xl p-7 border border-[#EAE8E4] flex flex-col justify-between hover:shadow-lg transition-all">
              <div>
                <div className="w-12 h-12 rounded-full bg-[#ECFDF5] text-[#059669] grid place-items-center mb-6">
                  <Sparkles size={24} />
                </div>
                <p className="text-xs font-body uppercase tracking-[0.14em] text-[#059669] font-medium">100% Végétal</p>
                <h3 className="font-display text-2xl text-[#111111] mt-1">Extraits Botaniques</h3>
                <p className="text-xs text-[#666666] leading-relaxed mt-3">
                  Complexe d'eau florale apaisante et de bio-actifs calmants. Prévient toute irritation, même sur les peaux les plus sensibles et réactives.
                </p>
              </div>
              <div className="pt-6 mt-6 border-t border-[#F8F8F6] text-[11px] text-[#111111] font-medium">
                Action : Confort &amp; Tolérance
              </div>
            </Reveal>

          </div>
        </div>
      </section>

      {/* 5. BENEFITS SECTION (8 PILLARS) */}
      <section className="py-24 bg-white" id="benefits">
        <div className="container-queen">
          <Reveal className="text-center max-w-2xl mx-auto mb-16">
            <p className="label-eyebrow text-[#C5A880] mb-2">Efficacité Clinique</p>
            <h2 className="font-display text-[#111111] text-3xl sm:text-5xl">
              Les 8 Piliers de l'Éclat Royal
            </h2>
            <p className="text-[#666666] font-body text-base mt-4">
              Des bénéfices objectivés pour transformer visiblement la texture et la jeunesse de la peau.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "Éclat Naturel", desc: "Illumine instantanément les teints ternes et fatigués." },
              { title: "Hydratation Profonde", desc: "Maintient l'eau au cœur des cellules pendant 24h." },
              { title: "Action Anti-Âge", desc: "Lisse visiblement les ridules d'expression et de sécheresse." },
              { title: "Protection Antioxydante", desc: "Filtre les agressions environnementales et la pollution." },
              { title: "Barrière Renforcée", desc: "Répare et consolide le film hydrolipidique naturel." },
              { title: "Teint Uniformisé", desc: "Atténue les taches pigmentaires et les rougeurs diffuses." },
              { title: "Fermeté & Élasticité", desc: "Relance la synthèse de collagène et d'élastine." },
              { title: "Texture Lisse", desc: "Affine le grain de peau pour un toucher velouté et soyeux." },
            ].map((b, i) => (
              <Reveal key={b.title} delay={i * 60}>
                <div className="p-6 rounded-2xl border border-[#EAE8E4] bg-white hover:bg-[#F8F8F6] transition-colors h-full flex flex-col justify-between">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-[11px] font-mono text-[#999999]">0{i + 1}</span>
                    <CheckCircle2 size={16} className="text-[#111111]" />
                  </div>
                  <div>
                    <h4 className="font-display text-xl text-[#111111] mb-2">{b.title}</h4>
                    <p className="text-xs text-[#666666] leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 6. BEFORE & AFTER SECTION (INTERACTIVE SLIDER) */}
      <section className="py-24 bg-[#F8F8F6] border-y border-[#EAE8E4]" id="results">
        <div className="container-queen">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            
            <Reveal className="lg:col-span-5 space-y-6">
              <p className="label-eyebrow text-[#C5A880]">{t("results_badge")}</p>
              <h2 className="font-display text-[#111111] text-3xl sm:text-5xl leading-tight">
                {t("results_title")}
              </h2>
              <p className="text-[#666666] font-body text-base leading-relaxed">
                {t("results_subtitle")}
              </p>

              <div className="space-y-4 pt-4 border-t border-[#EAE8E4]">
                <div className="flex justify-between items-center py-2 border-b border-[#EAE8E4]/60">
                  <span className="font-body text-sm text-[#111111]">{t("stat_glow")}</span>
                  <span className="font-display text-xl text-[#111111] font-semibold">+94%</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-[#EAE8E4]/60">
                  <span className="font-body text-sm text-[#111111]">{t("stat_hydration")}</span>
                  <span className="font-display text-xl text-[#111111] font-semibold">+89%</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="font-body text-sm text-[#111111]">{t("stat_wrinkles")}</span>
                  <span className="font-display text-xl text-[#111111] font-semibold">-48%</span>
                </div>
              </div>

              <div className="pt-2">
                <Link to="/shop" className="btn-queen-primary">
                  {t("adopt_ritual")}
                </Link>
              </div>
            </Reveal>

            {/* Interactive Image Comparison Slider */}
            <Reveal className="lg:col-span-7">
              <div
                ref={sliderRef}
                className="relative aspect-[4/3] sm:aspect-[16/11] rounded-3xl overflow-hidden shadow-2xl border border-[#EAE8E4] cursor-ew-resize select-none bg-[#F4F1EA]"
                onMouseDown={() => setIsDragging(true)}
                onMouseUp={() => setIsDragging(false)}
                onMouseLeave={() => setIsDragging(false)}
                onMouseMove={handleMouseMove}
                onTouchMove={handleTouchMove}
              >
                {/* After Image (Full width background) */}
                <img
                  src={apresFace}
                  alt="Après 28 jours Sérum Vitamine C The Queen"
                  className="absolute inset-0 w-full h-full object-cover object-center"
                />
                <span className="absolute top-5 right-5 bg-black/70 text-white text-[10px] sm:text-[11px] font-body tracking-[0.16em] uppercase px-3.5 py-1.5 rounded-full z-10 backdrop-blur-md border border-white/20">
                  {t("after_label")}
                </span>

                {/* Before Image (Clipped by slider position) */}
                <div
                  className="absolute inset-y-0 left-0 overflow-hidden"
                  style={{ width: `${sliderPosition}%` }}
                >
                  <img
                    src={avantFace}
                    alt="Avant traitement The Queen - Jour 0"
                    className="absolute inset-0 w-full h-full object-cover object-center"
                    style={{ width: sliderRef.current?.clientWidth || "100%", maxWidth: "none" }}
                  />
                  <span className="absolute top-5 left-5 bg-black/70 text-white text-[10px] sm:text-[11px] font-body tracking-[0.16em] uppercase px-3.5 py-1.5 rounded-full z-10 backdrop-blur-md border border-white/20">
                    {t("before_label")}
                  </span>
                </div>

                {/* Drag Handle Bar */}
                <div
                  className="absolute inset-y-0 w-0.5 bg-white shadow-[0_0_15px_rgba(0,0,0,0.5)] flex items-center justify-center pointer-events-none"
                  style={{ left: `${sliderPosition}%` }}
                >
                  <div className="w-9 h-9 rounded-full bg-white text-[#111111] shadow-2xl border border-[#EAE8E4] flex items-center justify-center text-xs font-bold font-mono tracking-widest">
                    ↔
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 mt-4 px-1">
                <p className="text-[11px] text-[#777777] font-body">
                  ← {t("slider_instruction")}
                </p>
                <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#999999] bg-white px-2.5 py-1 rounded-md border border-[#EAE8E4]">
                  Photo Clinique Non Retouchée
                </span>
              </div>
            </Reveal>

          </div>
        </div>
      </section>

      {/* 7. SKINCARE ROUTINE (LE PROTOCOLE 24H) */}
      <section className="py-24 bg-white" id="routine">
        <div className="container-queen">
          <Reveal className="text-center max-w-2xl mx-auto mb-16">
            <p className="label-eyebrow text-[#C5A880] mb-2">Protocole Quotidien</p>
            <h2 className="font-display text-[#111111] text-3xl sm:text-5xl">
              Le Rituel de Beauté Royal
            </h2>
            <p className="text-[#666666] font-body text-base mt-4">
              L'alternance synergique du Sérum Vitamine C le matin et du Sérum Acide Hyaluronique le soir assure une protection et une régénération cutanée 24h/24.
            </p>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-10">
            
            {/* Morning Protocol */}
            <Reveal className="p-8 sm:p-10 rounded-3xl border border-[#EAE8E4] bg-[#FFFBF5] relative">
              <div className="flex items-center justify-between pb-6 border-b border-[#EAE8E4]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#FEF3C7] text-[#D97706] grid place-items-center">
                    <Sun size={20} />
                  </div>
                  <div>
                    <h3 className="font-display text-2xl text-[#111111]">Rituel Matin</h3>
                    <p className="text-xs text-[#666666]">Bouclier antioxydant &amp; Éclat solaire</p>
                  </div>
                </div>
                <span className="text-xs font-mono uppercase text-[#D97706] font-semibold tracking-wider">Étape 01</span>
              </div>

              <div className="mt-8 space-y-6">
                <div className="flex items-start gap-4">
                  <span className="w-6 h-6 rounded-full bg-white border border-[#EAE8E4] text-[#111111] text-xs grid place-items-center shrink-0 font-medium mt-0.5">1</span>
                  <div>
                    <p className="text-sm font-semibold text-[#111111]">Nettoyer la peau</p>
                    <p className="text-xs text-[#666666] mt-0.5">Utilisez un nettoyant doux à pH neutre pour éliminer les impuretés nocturnes.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <span className="w-6 h-6 rounded-full bg-[#D97706] text-white text-xs grid place-items-center shrink-0 font-medium mt-0.5">2</span>
                  <div>
                    <p className="text-sm font-semibold text-[#111111]">Appliquer le Sérum Vitamine C</p>
                    <p className="text-xs text-[#666666] mt-0.5">Déposez 3 à 4 gouttes sur le visage et le cou. Tapotez délicatement du bout des doigts.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <span className="w-6 h-6 rounded-full bg-white border border-[#EAE8E4] text-[#111111] text-xs grid place-items-center shrink-0 font-medium mt-0.5">3</span>
                  <div>
                    <p className="text-sm font-semibold text-[#111111]">Protéger avec un écran solaire SPF 50</p>
                    <p className="text-xs text-[#666666] mt-0.5">La Vitamine C décuple l'efficacité des filtres solaires contre les taches.</p>
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Evening Protocol */}
            <Reveal delay={150} className="p-8 sm:p-10 rounded-3xl border border-[#EAE8E4] bg-[#F5F9FC] relative">
              <div className="flex items-center justify-between pb-6 border-b border-[#EAE8E4]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#E0F2FE] text-[#0284C7] grid place-items-center">
                    <Moon size={20} />
                  </div>
                  <div>
                    <h3 className="font-display text-2xl text-[#111111]">Rituel Soir</h3>
                    <p className="text-xs text-[#666666]">Réparation profonde &amp; Hydratation cellulaire</p>
                  </div>
                </div>
                <span className="text-xs font-mono uppercase text-[#0284C7] font-semibold tracking-wider">Étape 02</span>
              </div>

              <div className="mt-8 space-y-6">
                <div className="flex items-start gap-4">
                  <span className="w-6 h-6 rounded-full bg-white border border-[#EAE8E4] text-[#111111] text-xs grid place-items-center shrink-0 font-medium mt-0.5">1</span>
                  <div>
                    <p className="text-sm font-semibold text-[#111111]">Démaquiller et purifier</p>
                    <p className="text-xs text-[#666666] mt-0.5">Libérez les pores des particules de pollution accumulées au cours de la journée.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <span className="w-6 h-6 rounded-full bg-[#0284C7] text-white text-xs grid place-items-center shrink-0 font-medium mt-0.5">2</span>
                  <div>
                    <p className="text-sm font-semibold text-[#111111]">Appliquer le Sérum Acide Hyaluronique</p>
                    <p className="text-xs text-[#666666] mt-0.5">Sur peau encore légèrement humidifiée, appliquez 3 gouttes pour sceller l'hydratation.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <span className="w-6 h-6 rounded-full bg-white border border-[#EAE8E4] text-[#111111] text-xs grid place-items-center shrink-0 font-medium mt-0.5">3</span>
                  <div>
                    <p className="text-sm font-semibold text-[#111111]">Crème de nuit régénérante</p>
                    <p className="text-xs text-[#666666] mt-0.5">Laissez infuser les actifs tout au long de votre cycle de sommeil réparateur.</p>
                  </div>
                </div>
              </div>
            </Reveal>

          </div>
        </div>
      </section>

      {/* 8. TESTIMONIALS (VERIFIED CUSTOMERS & EXPERTS) */}
      <section className="py-24 bg-[#F8F8F6] border-t border-[#EAE8E4]">
        <div className="container-queen">
          <Reveal className="text-center max-w-xl mx-auto mb-16">
            <p className="label-eyebrow text-[#C5A880] mb-2">Témoignages &amp; Avis</p>
            <h2 className="font-display text-[#111111] text-3xl sm:text-5xl">
              L'Approbation Royale
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                author: "Dr. Amina B.",
                role: "Dermatologue",
                location: "Alger",
                text: "La formulation du Sérum Vitamine C The Queen est remarquable. La stabilité de l'acide ascorbique est parfaitement maîtrisée, offrant une tolérance irréprochable et un éclat spectaculaire.",
              },
              {
                author: "Yasmine K.",
                role: "Cliente Vérifiée",
                location: "Oran",
                text: "J'utilise Le Duo Royal depuis 3 semaines. Mes ridules de déshydratation ont disparu et mes amies me demandent quel soin j'ai fait en institut ! Livraison rapide à domicile.",
              },
              {
                author: "Selma M.",
                role: "Cliente Vérifiée",
                location: "Constantine",
                text: "La texture de l'Acide Hyaluronique ne colle pas du tout, elle pénètre instantanément. La sensation de confort dure toute la journée. Un vrai produit de luxe.",
              },
            ].map((t, idx) => (
              <Reveal key={t.author} delay={idx * 100}>
                <div className="bg-white p-8 rounded-3xl border border-[#EAE8E4] shadow-sm flex flex-col justify-between h-full">
                  <div>
                    <div className="flex gap-1 text-[#C5A880] mb-5">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={15} fill="#C5A880" />
                      ))}
                    </div>
                    <p className="font-display text-lg text-[#111111] italic leading-relaxed">
                      « {t.text} »
                    </p>
                  </div>
                  <div className="pt-6 mt-6 border-t border-[#F8F8F6] flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-[#111111]">{t.author}</p>
                      <p className="text-xs text-[#888888]">{t.role} · {t.location}</p>
                    </div>
                    <CheckCircle2 size={18} className="text-[#C5A880]" />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 9. ALGERIAN LOGISTICS & ASSURANCE BANNER */}
      <section className="py-20 bg-white">
        <div className="container-queen">
          <div className="bg-[#111111] text-white rounded-3xl p-8 sm:p-14 relative overflow-hidden">
            {/* Ambient glows */}
            <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#C5A880]/15 blur-3xl pointer-events-none" />

            <div className="relative z-10 grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8 space-y-4">
                <p className="label-eyebrow text-[#C5A880]">Expédition Premium en Algérie</p>
                <h3 className="font-display text-3xl sm:text-5xl leading-tight">
                  Livraison dans les 58 Wilayas.<br />
                  Paiement sécurisé à la réception.
                </h3>
                <p className="text-sm text-[#AAAAAA] max-w-xl leading-relaxed">
                  Recevez votre coffret The Queen directement à votre domicile ou retirez-le au bureau Stop-Desk le plus proche. Vous ne réglez votre commande qu'au moment où le colis est entre vos mains.
                </p>
              </div>

              <div className="lg:col-span-4 flex flex-col gap-3">
                <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-4 flex items-center gap-3">
                  <Truck size={22} className="text-[#C5A880]" />
                  <div>
                    <p className="text-xs font-semibold text-white">Livraison 58 Wilayas</p>
                    <p className="text-[11px] text-[#BBBBBB]">À domicile ou en Stop Desk</p>
                  </div>
                </div>
                <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-4 flex items-center gap-3">
                  <Banknote size={22} className="text-[#C5A880]" />
                  <div>
                    <p className="text-xs font-semibold text-white">Paiement à la livraison</p>
                    <p className="text-[11px] text-[#BBBBBB]">En espèces à la remise du colis</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
